import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  CircularProgress,
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton, // Added for the remove button icon
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DeleteIcon from '@mui/icons-material/Delete'; // Icon for remove button
import { useNotification } from './context/NotificationContext';

// Define the API base URL for the Flask action orchestrator
const API_BASE_URL = 'https://frek.cc/proxy/10.147.19.189:9922';

// --- Data from your GoIP App ---
const GOIP_BASE_URL = 'https://frek.cc/proxy/38.54.86.24:7687';

const gateways = ['g1', 'g2', 'g3', 'g4', 'g5', 'g6', 'g7', 'g8', 'g9'];

  const sipOptions = [
    { key: '-0', label: 'Off Traffic (Global)' },
    { key: '0', label: 'Empty Account (Global)' },
    { key: '1', label: 'NCLI Route On' },
    { key: '1a', label: 'NCLI Route Accounts' },
    { key: 'CC1', label: 'CC1 On(E)' },
    { key: 'CC1A', label: 'CC1 Accounts(E)' },
    { key: 'CC2', label: 'CC2 On(Ea)' },
    { key: 'CC2A', label: 'CC2 Accounts(Ea)' },
    { key: 'CC3', label: 'CC3 On(F)' },
    { key: 'CC3A', label: 'CC3 Accounts(F)' },
    { key: 'CC4', label: 'CC4 On(Fa)' },
    { key: 'CC4A', label: 'CC4 Accounts(Fa)' },
    { key: 'CC5', label: 'CC5 On(Eb)' },
    { key: 'CC5A', label: 'CC5 Accounts(Eb)' },
    { key: 'CC6', label: 'CC6 On(Fb)' },
    { key: 'CC6A', label: 'CC6 Accounts(Fb)' },
    { key: 'CC7', label: 'CC7 On(Fc)' },
    { key: 'CC7A', label: 'CC7 Accounts(Fc)' },
    { key: 'CC8', label: 'CC8 On(Ec)' },
    { key: 'CC8A', label: 'CC8 Accounts(Ec)' },
    { key: 'CC9', label: 'CC9 On(Ed)' },
    { key: 'CC9A', label: 'CC9 Accounts(Ed)' },
    { key: 'CC10', label: 'CC10 On(Fd)' },
    { key: 'CC10A', label: 'CC10 Accounts(Fd)' },
    { key: 'CC11', label: 'CC11 On(Ee)' },
    { key: 'CC11A', label: 'CC11 Accounts(Ee)' },
    { key: 'CC12', label: 'CC12 On(Fe)' },
    { key: 'CC12A', label: 'CC12 Accounts(Fe)' },
    { key: 'CC13', label: 'CC13 On(Ef)' },
    { key: 'CC13A', label: 'CC13 Accounts(Ef)' },
    { key: 'CC14', label: 'CC14 On(Ff)' },
    { key: 'CC14A', label: 'CC14 Accounts(Ff)' },

  ];

const imeiSmsOperations = [
  { key: 'change-imei', label: 'Change IMEI (All)' },
  { key: 'change-imei-no-sim', label: 'Change IMEI (only port without sim)' },
  { key: 'enable-sms', label: 'Enable SMS' },
  { key: 'disable-sms', label: 'Disable SMS' },
  { key: 'send-ussd', label: 'Send USSD (Default)' },
  { key: 'both-on', label: 'Both Locker ON' },
  { key: 'on-sms-locker', label: 'SMS Locker ON' },
  { key: 'on-call-locker', label: 'Call Locker ON' },
  { key: 'both-off', label: 'Both Locker OFF' },
  { key: 'sms-auto-on', label: 'SMS Auto Test On' },
  { key: 'sms-auto-off', label: 'SMS Auto Test Off' },
  { key: 'update-cookie', label: 'Update Cookie' },
  { key: 'save-config', label: 'Save Config' },
  { key: 'reboot-gateway', label: 'Reboot Gateway' }
];

function ActionOrchestratorFrontend() {
  const { showNotification } = useNotification();
  const [customActionUrl, setCustomActionUrl] = useState('');
  const [actionsList, setActionsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem('darkMode');
      return savedMode === 'true' ? true : false;
    } catch (error) {
      console.warn("Could not read darkMode from localStorage, defaulting to light mode:", error);
      return false;
    }
  });

  const [actionType, setActionType] = useState('customUrl');
  const [selectedSipKey, setSelectedSipKey] = useState('');
  const [selectedImeiKey, setSelectedImeiKey] = useState('');
  const [selectedGatewayForGoip, setSelectedGatewayForGoip] = useState(gateways[0] || '');

  const theme = useMemo(() =>
    createTheme({
      palette: {
        mode: darkMode ? 'dark' : 'light',
        primary: { main: darkMode ? '#90caf9' : '#2196f3', dark: darkMode ? '#64b5f6' : '#1976d2' },
        secondary: { main: darkMode ? '#f48fb1' : '#dc004e', dark: darkMode ? '#f06292' : '#9a0036' },
        success: { main: darkMode ? '#81c784' : '#4caf50', dark: darkMode ? '#66bb6a' : '#388e3c' },
        background: { default: darkMode ? '#121212' : '#f4f6f8', paper: darkMode ? '#1e1e1e' : '#ffffff' },
        text: { primary: darkMode ? '#ffffff' : '#212121', secondary: darkMode ? '#b0b0b0' : '#757575' },
        divider: darkMode ? '#424242' : '#e0e0e0',
        error: { main: darkMode ? '#f44336' : '#d32f2f' }, // Added error color for remove button
      },
      typography: {
        h4: { color: darkMode ? '#bbdefb' : '#004d40', fontWeight: 'bold', fontSize: '2.125rem', '@media (max-width:600px)': { fontSize: '1.75rem' } },
        h5: { color: darkMode ? '#a7d9ff' : '#004d40', fontSize: '1.5rem', '@media (max-width:600px)': { fontSize: '1.25rem' } }
      },
      components: {
        MuiButton: {
          styleOverrides: {
            contained: ({ theme, ownerState }) => ({
              backgroundColor: ownerState.color === 'primary' ? theme.palette.primary.main :
                ownerState.color === 'success' ? theme.palette.success.main :
                  ownerState.color === 'error' ? theme.palette.error.main : undefined,
              '&:hover': {
                backgroundColor: ownerState.color === 'primary' ? theme.palette.primary.dark :
                  ownerState.color === 'success' ? theme.palette.success.dark :
                    ownerState.color === 'error' ? theme.palette.error.dark : undefined,
              },
              color: theme.palette.getContrastText(
                ownerState.color === 'primary' ? theme.palette.primary.main :
                  ownerState.color === 'success' ? theme.palette.success.main :
                    ownerState.color === 'error' ? theme.palette.error.main : theme.palette.grey[500]
              ),
            }),
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: ({ theme }) => ({
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme.palette.divider, },
                '&:hover fieldset': { borderColor: theme.palette.primary.main, },
                '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, },
              },
              '& .MuiInputLabel-root': { color: theme.palette.text.secondary, },
              '& .MuiInputBase-input': { color: theme.palette.text.primary, },
              '& .MuiFormHelperText-root': { color: theme.palette.text.secondary, },
            }),
          },
        },
        MuiList: { styleOverrides: { root: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }) } },
        MuiListItem: { styleOverrides: { root: ({ theme }) => ({ borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }) } },
        MuiAlert: {
          styleOverrides: {
            root: ({ theme }) => ({
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : undefined,
              color: theme.palette.text.primary,
              '& .MuiAlert-icon': { color: theme.palette.primary.main + ' !important' },
            }),
          },
        },
      },
    }), [darkMode]
  );

  useEffect(() => {
    loadActions();
    document.title = 'Action Orchestrator';
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('darkMode', darkMode.toString());
    } catch (error) {
      console.warn("Could not save darkMode to localStorage:", error);
    }
  }, [darkMode]);

  const handleNotification = (message, type = 'info') => {
    showNotification(message, type);
  };

  const loadActions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/get_actions`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setActionsList(data.actions || []);
    } catch (error) {
      console.error('Error fetching actions:', error);
      handleNotification(`Failed to load actions: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAction = async () => {
    let urlToAdd = '';
    let operationDescription = '';

    // Determine the correct GOIP base URL based on the selected gateway
    let currentGoipBaseUrl = GOIP_BASE_URL;
    if (selectedGatewayForGoip === 'g9') {
        currentGoipBaseUrl = 'https://frek.cc/proxy/38.54.86.24:6687';
    }

    if (actionType === 'customUrl') {
      if (!customActionUrl.trim()) {
        handleNotification('Please enter a URL for the custom action.', 'warning');
        return;
      }
      urlToAdd = customActionUrl.trim();
      operationDescription = urlToAdd;
    } else if (actionType === 'sipOp') {
      if (!selectedSipKey || !selectedGatewayForGoip) {
        handleNotification('Please select a SIP operation and a gateway.', 'warning');
        return;
      }
      // Use the dynamically selected base URL
      urlToAdd = `${currentGoipBaseUrl}/configure-sip/${selectedGatewayForGoip}/${selectedSipKey}`;
      const opLabel = sipOptions.find(op => op.key === selectedSipKey)?.label || selectedSipKey;
      operationDescription = `SIP: ${opLabel} on ${selectedGatewayForGoip.toUpperCase()}`;
    } else if (actionType === 'imeiOp') {
      if (!selectedImeiKey || !selectedGatewayForGoip) {
        handleNotification('Please select an IMEI/SMS operation and a gateway.', 'warning');
        return;
      }
      const opLabel = imeiSmsOperations.find(op => op.key === selectedImeiKey)?.label || selectedImeiKey;
      operationDescription = `GoIP: ${opLabel} on ${selectedGatewayForGoip.toUpperCase()}`;

      // Use the dynamically selected base URL
      switch (selectedImeiKey) {
        case 'save-config': urlToAdd = `${currentGoipBaseUrl}/save/${selectedGatewayForGoip}`; break;
        case 'reboot-gateway': urlToAdd = `${currentGoipBaseUrl}/reboot/${selectedGatewayForGoip}`; break;
        case 'update-cookie': urlToAdd = `${currentGoipBaseUrl}/update-local-cookies/${selectedGatewayForGoip}`; break;
        case 'send-ussd': urlToAdd = `${currentGoipBaseUrl}/send-ussd/${selectedGatewayForGoip}`; break;
        default: urlToAdd = `${currentGoipBaseUrl}/${selectedImeiKey}/${selectedGatewayForGoip}`;
      }
    }

    if (!urlToAdd) {
      handleNotification('Could not determine action URL.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/add_action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlToAdd, description: operationDescription })
      });
      if (!response.ok) { // More detailed error reporting from backend if available
        const errorData = await response.json().catch(() => ({})); // Try to parse error response
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      handleNotification(data.message || `Action "${operationDescription}" added.`, 'success');
      setCustomActionUrl('');
      loadActions();
    } catch (error) {
      console.error('Error adding action:', error);
      handleNotification(`Failed to add action: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // --- New function to remove an action ---
  const handleRemoveAction = async (actionIdToRemove) => {
    // Optional: Add a confirmation dialog here if desired
    // const confirmRemove = window.confirm("Are you sure you want to remove this action?");
    // if (!confirmRemove) return;

    setIsLoading(true); // You might want a specific state like `isRemoving`
    try {
      const response = await fetch(`${API_BASE_URL}/remove_action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: actionIdToRemove })
      });
      const data = await response.json(); // Always try to parse JSON
      if (response.ok && data.status === 'success') {
        handleNotification(data.message, 'success');
        loadActions(); // Refresh the list
      } else {
        throw new Error(data.message || `Failed to remove action. Status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error removing action:', error);
      handleNotification(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  // --- End new function ---

  const handleTriggerActions = async () => {
    if (actionsList.length === 0) {
      handleNotification('No actions registered to trigger.', 'info');
      return;
    }
    setIsTriggering(true);
    try {
      const response = await fetch(`${API_BASE_URL}/trigger_actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      handleNotification(data.message, 'success');
      // Backend clears the list, so frontend should reflect this by reloading or setting to empty
      setActionsList([]); // Or call loadActions() if you want to fetch potentially new state from backend
    } catch (error) {
      console.error('Error triggering actions:', error);
      handleNotification(`Failed to trigger actions: ${error.message}`, 'error');
    } finally {
      setIsTriggering(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={darkMode} onChange={() => setDarkMode(!darkMode)} icon={<Brightness7Icon />} checkedIcon={<Brightness4Icon />} color="default" />}
            label={darkMode ? "Dark Mode" : "Light Mode"}
            sx={{ color: 'text.primary' }}
          />
        </Box>

        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
          Action Triggers
        </Typography>

        <Box sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 4, backgroundColor: 'background.paper' }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>Add New Action</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="action-type-label">Action Type</InputLabel>
                <Select labelId="action-type-label" value={actionType} onChange={(e) => setActionType(e.target.value)} label="Action Type">
                  <MenuItem value="customUrl">Custom URL</MenuItem>
                  <MenuItem value="sipOp">GoIP SIP Operation</MenuItem>
                  <MenuItem value="imeiOp">GoIP IMEI/SMS Operation</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {actionType === 'customUrl' && (
              <Grid item xs={12}>
                <TextField label="Custom Action URL" variant="outlined" value={customActionUrl} onChange={(e) => setCustomActionUrl(e.target.value)} fullWidth required helperText="e.g., https://api.example.com/do_something" />
              </Grid>
            )}

            {(actionType === 'sipOp' || actionType === 'imeiOp') && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="gateway-select-label">Target Gateway</InputLabel>
                    <Select labelId="gateway-select-label" value={selectedGatewayForGoip} onChange={(e) => setSelectedGatewayForGoip(e.target.value)} label="Target Gateway">
                      {gateways.map(gw => (<MenuItem key={gw} value={gw}>{gw.toUpperCase()}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="operation-select-label">{actionType === 'sipOp' ? 'SIP Operation' : 'IMEI/SMS Operation'}</InputLabel>
                    <Select labelId="operation-select-label" value={actionType === 'sipOp' ? selectedSipKey : selectedImeiKey} onChange={(e) => actionType === 'sipOp' ? setSelectedSipKey(e.target.value) : setSelectedImeiKey(e.target.value)} label={actionType === 'sipOp' ? 'SIP Operation' : 'IMEI/SMS Operation'}>
                      {(actionType === 'sipOp' ? sipOptions : imeiSmsOperations).map(op => (<MenuItem key={op.key} value={op.key}>{op.label}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button variant="contained" color="success" onClick={handleAddAction} fullWidth sx={{ py: { xs: 1, md: 1.5 }, fontSize: { xs: '0.9rem', md: '1rem' } }} disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Add Action'}
              </Button>
            </Grid>
          </Grid>
        </Box>



        <Box sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>Registered Actions ({actionsList.length})</Typography>
          {isLoading && actionsList.length === 0 ? (
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <CircularProgress size={20} color="inherit" /> Loading actions...
            </Typography>
          ) : (
            <List sx={{ minHeight: 100 }}>
              {actionsList.length === 0 ? (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No actions registered yet. Add some above!</Typography>
              ) : (
                actionsList.map((action) => ( // Removed index as key, using action.id instead
                  <ListItem
                    key={action.id} // Use the unique ID from the backend
                    secondaryAction={ // Add remove button here
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveAction(action.id)}
                        disabled={isLoading || isTriggering} // Disable if any global loading is active
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                    sx={{ borderBottom: '1px solid', borderBottomColor: 'divider', '&:last-child': { borderBottom: 'none' } }}
                  >
                    <ListItemText
                      primary={action.description}
                      secondary={action.url}
                      primaryTypographyProps={{ color: 'text.primary', style: { overflowWrap: 'break-word' } }}
                      secondaryTypographyProps={{ color: 'text.secondary', style: { overflowWrap: 'break-word', fontSize: '0.8rem' } }}
                    />
                  </ListItem>
                ))
              )}
            </List>

          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleTriggerActions}
            fullWidth
            disabled={actionsList.length === 0 || isTriggering}
            sx={{ mt: 2, py: 1.5, fontSize: '1rem', fontWeight: 'bold' }}
          >
            {isTriggering ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Trigger All (${actionsList.length}) Actions`
            )}
          </Button>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default ActionOrchestratorFrontend;
